import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { MEDICAL_EXAM_CATEGORIES } from '../../../constants/medicalExamCategories';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import { EXAM_STATUS_LABELS, formatMonthKey } from './reportUtils';
import ReportSectionHead from './ReportSectionHead';
import StatisticalDataTable, { statNumberColumn, statPercentColumn, statRankColumn } from './StatisticalDataTable';
import { HeadDoctorStatCard } from '../ui';
import {
  ClinicalBarChart,
  ClinicalDoughnutChart,
  ClinicalLineChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import { CHART_PALETTE, EXAM_STATUS_COLORS } from './charts/chartTheme';
import {
  computeShares,
  doughnutChartData,
  horizontalBarChartData,
  labeledPieData,
  lineChartData,
  topNWithOthers,
  truncateLabel
} from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

function examCategoryLabel(category) {
  const cfg = MEDICAL_EXAM_CATEGORIES[String(category || '').toUpperCase()];
  return cfg?.labelPlural || cfg?.label || category || '—';
}

const EXAM_CATEGORY_LABELS = Object.fromEntries(
  Object.values(MEDICAL_EXAM_CATEGORIES).map((c) => [c.id, c.labelPlural || c.label])
);

export default function HeadDoctorReportsExamensPage() {
  const { data, summary, loading } = useClinicalReports();
  const byCategory = data?.exams?.by_category || [];
  const byStatus = data?.exams?.by_status || [];
  const byMonth = data?.exams?.by_month || [];
  const rows = useMemo(
    () => computeShares(data?.exams?.ranking || [], 'order_count'),
    [data]
  );

  const categoryPie = useMemo(
    () =>
      labeledPieData(byCategory, {
        labelKey: 'category',
        labelMap: EXAM_CATEGORY_LABELS,
        colorMap: {}
      }),
    [byCategory]
  );

  const statusPie = useMemo(
    () =>
      labeledPieData(byStatus, {
        labelKey: 'status',
        labelMap: EXAM_STATUS_LABELS,
        colorMap: EXAM_STATUS_COLORS
      }),
    [byStatus]
  );

  const trendData = useMemo(
    () =>
      lineChartData(
        byMonth.map((row) => formatMonthKey(row.month_key)),
        [
          {
            label: COPY.CHART_EXAMS,
            data: byMonth.map((row) => Number(row.count)),
            color: CHART_PALETTE[2],
            fill: true
          }
        ]
      ),
    [byMonth]
  );

  const examBar = useMemo(
    () =>
      horizontalBarChartData(
        rows.slice(0, 10).map((r) => truncateLabel(r.exam_name, 26)),
        rows.slice(0, 10).map((r) => Number(r.order_count)),
        COPY.CHART_EXAM_ORDERS
      ),
    [rows]
  );

  const examPie = useMemo(() => {
    const pie = topNWithOthers(rows, 'order_count', 'exam_name', 5, COPY.CHART_OTHERS);
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [rows]);

  const tableColumns = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    { key: 'exam_name', label: COPY.COL_EXAM, className: 'fw-semibold', render: (r) => r.exam_name },
    {
      key: 'exam_category',
      label: COPY.COL_EXAM_CATEGORY,
      render: (r) => examCategoryLabel(r.exam_category)
    },
    statRankColumn(COPY.COL_EXAM_ORDERS, 'order_count'),
    statPercentColumn(COPY.COL_SHARE),
    statNumberColumn(COPY.COL_CONSULTATIONS, 'consultation_count')
  ];

  const empty = summary.exam_orders === 0;

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_EXAMENS}
        title={COPY.PAGE_EXAMENS_TITLE}
        description={COPY.PAGE_EXAMENS_DESC}
      />

      <Row className="g-3 mb-4">
        <Col sm={6}>
          <HeadDoctorStatCard title={COPY.KPI_EXAM_ORDERS} value={summary.exam_orders} icon="chartBar" tone="indigo" loading={loading} />
        </Col>
        <Col sm={6}>
          <HeadDoctorStatCard title={COPY.KPI_EXAM_TYPES} value={summary.unique_exams} icon="desktop" tone="teal" loading={loading} />
        </Col>
      </Row>

      {loading ? (
        <div className="hd-reports-panel mb-4"><div className="hd-reports-loading"><span className="spinner-border spinner-border-sm" /><span>{COPY.LOADING}</span></div></div>
      ) : empty ? (
        <div className="hd-reports-panel mb-4"><p className="hd-reports-empty mb-0">{COPY.EMPTY_EXAMS}</p></div>
      ) : (
        <>
          <ReportsChartGrid>
            <ReportsChartCol lg={4}>
              <ReportChartCard title={COPY.CHART_EXAM_CATEGORY_TITLE} subtitle={COPY.CHART_EXAM_CATEGORY_DESC} height={280}>
                <ClinicalDoughnutChart data={doughnutChartData(categoryPie.labels, categoryPie.values, categoryPie.colors)} />
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={4}>
              <ReportChartCard title={COPY.CHART_EXAM_STATUS_TITLE} subtitle={COPY.CHART_EXAM_STATUS_DESC} height={280}>
                <ClinicalDoughnutChart data={doughnutChartData(statusPie.labels, statusPie.values, statusPie.colors)} />
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={4}>
              <ReportChartCard title={COPY.CHART_EXAM_TREND_TITLE} subtitle={COPY.CHART_EXAM_TREND_DESC} height={280}>
                <ClinicalLineChart data={trendData} xLabel={COPY.CHART_AXIS_PERIOD} yLabel={COPY.CHART_AXIS_EXAMS} />
              </ReportChartCard>
            </ReportsChartCol>
          </ReportsChartGrid>

          {rows.length > 0 && (
            <ReportsChartGrid>
              <ReportsChartCol lg={7}>
                <ReportChartCard title={COPY.CHART_EXAM_BAR_TITLE} subtitle={COPY.CHART_EXAM_BAR_DESC} height={320}>
                  <ClinicalBarChart data={examBar} horizontal xLabel={COPY.CHART_AXIS_EXAMS} />
                </ReportChartCard>
              </ReportsChartCol>
              <ReportsChartCol lg={5}>
                <ReportChartCard title={COPY.CHART_EXAM_PIE_TITLE} subtitle={COPY.CHART_EXAM_PIE_DESC} height={320}>
                  <ClinicalDoughnutChart data={examPie} />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          )}
        </>
      )}

      {rows.length > 0 && (
        <>
          <h3 className="hd-reports-table-title">{COPY.TABLE_EXAM_TITLE}</h3>
          <StatisticalDataTable
            columns={tableColumns}
            rows={rows}
            rowKey="exam_id"
            maxValueKey="order_count"
            loading={loading}
            emptyLabel={COPY.EMPTY_EXAMS}
            loadingLabel={COPY.LOADING}
          />
        </>
      )}
    </>
  );
}
